import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@mantis-sas/ui";
import { Building, DollarSign, TrendingUp, Users } from "lucide-react";

const CostCenterAllocationChart = ({ data, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Asignaciones por Centro de Costo</CardTitle>
          <CardDescription>Cargando datos...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Asignaciones por Centro de Costo</CardTitle>
          <CardDescription>No hay datos disponibles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <p>No hay asignaciones de centros de costo disponibles</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Procesar datos para el gráfico
  const costCenterData = {};

  data.forEach((allocation) => {
    const costCenterId = allocation.cost_center_id;
    const costCenterName =
      allocation.cost_center_name ||
      allocation.cost_center?.name ||
      `CC-${costCenterId?.slice(-4)}`;
    const amount = parseFloat(allocation.amount || 0);
    const percentage = parseFloat(allocation.allocation_percentage || 0);
    const currency = allocation.currency || "USD";

    if (!costCenterData[costCenterId]) {
      costCenterData[costCenterId] = {
        id: costCenterId,
        name: costCenterName,
        totalAmount: 0,
        totalPercentage: 0,
        allocationCount: 0,
        currency: currency,
        allocations: [],
      };
    }

    costCenterData[costCenterId].totalAmount += amount;
    costCenterData[costCenterId].totalPercentage += percentage;
    costCenterData[costCenterId].allocationCount += 1;
    costCenterData[costCenterId].allocations.push(allocation);
  });

  // Convertir a array y ordenar por monto total
  const chartData = Object.values(costCenterData)
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 10); // Top 10

  // Datos para el gráfico de pastel (top 5)
  const pieData = chartData.slice(0, 5).map((item, index) => ({
    name:
      item.name.length > 15 ? item.name.substring(0, 15) + "..." : item.name,
    value: item.totalAmount,
    percentage: item.totalPercentage,
    count: item.allocationCount,
    color: `hsl(${(index * 72) % 360}, 70%, 50%)`,
  }));

  // Calcular estadísticas generales
  const totalAllocations = data.length;
  const totalAmount = data.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );
  const uniqueCostCenters = Object.keys(costCenterData).length;
  const averageAllocation =
    totalAllocations > 0 ? totalAmount / totalAllocations : 0;

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage) => {
    return `${parseFloat(percentage).toFixed(1)}%`;
  };

  return (
    <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center">
            <Building className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg text-slate-900 dark:text-slate-100">
              Asignaciones por Centro de Costo
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-400">
              Distribución de montos y porcentajes por centro de costo
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Estadísticas generales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Building className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                Centros de Costo
              </span>
            </div>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
              {uniqueCostCenters}
            </p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Total Asignado
              </span>
            </div>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
              {formatCurrency(totalAmount)}
            </p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                Asignaciones
              </span>
            </div>
            <p className="text-lg font-bold text-green-700 dark:text-green-300">
              {totalAllocations}
            </p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                Promedio
              </span>
            </div>
            <p className="text-lg font-bold text-orange-700 dark:text-orange-300">
              {formatCurrency(averageAllocation)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico de barras - Top centros de costo */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Top 10 Centros de Costo por Monto
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={100}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-4 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                              {data.name}
                            </p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Monto Total:</span>{" "}
                              {formatCurrency(data.totalAmount, data.currency)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">
                                Porcentaje Total:
                              </span>{" "}
                              {formatPercentage(data.totalPercentage)}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              <span className="font-medium">Asignaciones:</span>{" "}
                              {data.allocationCount}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="totalAmount"
                  name="Monto Total"
                  fill="url(#purpleGradient)"
                  radius={[0, 4, 4, 0]}
                />
                <defs>
                  <linearGradient
                    id="purpleGradient"
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#7c3aed" stopOpacity={1} />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de pastel - Top 5 centros de costo */}
          <div className="h-80">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Top 5 Centros de Costo
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: data.color }}
                            ></div>
                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                              {data.name}
                            </p>
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Monto: {formatCurrency(data.value)}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Porcentaje: {formatPercentage(data.percentage)}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            Asignaciones: {data.count}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tabla de detalles */}
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            Detalles por Centro de Costo
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-2 px-3 font-medium text-slate-700 dark:text-slate-300">
                    Centro de Costo
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700 dark:text-slate-300">
                    Monto Total
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700 dark:text-slate-300">
                    Porcentaje
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-700 dark:text-slate-300">
                    Asignaciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 px-3 text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: pieData[index]?.color || "#6b7280",
                          }}
                        ></div>
                        {item.name}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-slate-900 dark:text-slate-100">
                      {formatCurrency(item.totalAmount, item.currency)}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600 dark:text-slate-400">
                      {formatPercentage(item.totalPercentage)}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600 dark:text-slate-400">
                      {item.allocationCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CostCenterAllocationChart;
